import { INLINE_BOOKMARK_PLUGIN_ID } from './types';
import { PARAGRAPH_PLUGIN_ID } from '../paragraph/types';
import type { InlineBookmarkPluginElement } from './types';
import { Transforms, Editor, Node, Point, Range } from 'domains/reporter/RichTextEditor/core';
import { Element, NodeEntry, Path, Selection } from 'slate';
import { isSquareBracketType, BRACKET_TYPES } from 'domains/reporter/RichTextEditor/constants';
import { areMultipleNodesSelected } from '../../utils/areMultipleNodesSelected';
import { PLACEHOLDER_PLUGIN_ID } from '../placeholder/types';
import { HEADING_PLUGIN_ID } from '../heading/types';
import { walkSlateContent } from '../../utils/walkSlateContent';
import type { SlateContent } from '../../types';

export const createInlineBookmark = (text: string = ''): InlineBookmarkPluginElement => ({
  type: INLINE_BOOKMARK_PLUGIN_ID,
  children: [{ text: text }],
});

export const splitInlineBookmark = (editor: Editor): void => {
  Transforms.splitNodes(editor, {
    always: true,
    match: (n) => Element.isElement(n) && n.type === PARAGRAPH_PLUGIN_ID,
    mode: 'highest',
  });
  if (editor.selection != null) {
    Transforms.setNodes(
      editor,
      { type: INLINE_BOOKMARK_PLUGIN_ID, picklistID: undefined },
      {
        at: editor.selection,
        match: (n) => Element.isElement(n) && isSquareBracketType(n.type),
      }
    );
  }
};

export const insertInlineBookmark = (
  editor: Editor,
  options?: {
    location?: Selection;
    select?: boolean;
  }
): void => {
  const { selection } = editor;
  const location = options?.location ?? selection;
  if (location == null) {
    return;
  }

  const [fragment, insertLocation] = prepFragmentAndInsertionPoint(
    editor,
    [createInlineBookmark()],
    true,
    {
      location,
      split: true,
    }
  );

  if (insertLocation != null) {
    Transforms.insertNodes(editor, fragment, {
      at: insertLocation,
      select: options?.select ?? true,
    });
  }
};
/**
 * Handle inserting a fragment inside brackets if not able to render nested fields:
 * - If the brackets are empty + param is provided, remove the brackets and insert the fragment
 * - If the brackets are non-empty
 *   - And the fragment contains only text nodes, insert inline (for both single or multiline fragments)
 *   - And the fragment contains fields (picklists, bookmarks, placeholders), insert after the brackets
 *
 * If you are able to render nested fields, this logic is only run if the containing bracket is a placeholder
 */
export const prepFragmentAndInsertionPoint = (
  editor: Editor,
  fragment: Node[],
  _containsFields?: boolean,
  options?: {
    location?: Selection;
    removeEmptyBrackets?: boolean;
    split?: boolean;
  }
): [Node[], Path | Point | Range | null] => {
  const { selection } = editor;
  const insertionPoint = options?.location ?? selection;
  if (insertionPoint == null) {
    return [fragment, insertionPoint];
  }

  if (!areMultipleNodesSelected(editor)) {
    const [maybeBracketNode, maybeBracketLocation] = Editor.parent(editor, insertionPoint);

    if (!Element.isElement(maybeBracketNode)) return [fragment, insertionPoint];

    const isInsideBrackets = BRACKET_TYPES.includes(maybeBracketNode.type);

    if (maybeBracketNode.type === HEADING_PLUGIN_ID) {
      const locationAfter = Editor.after(editor, maybeBracketLocation);
      if (locationAfter != null) {
        return [fragment, locationAfter];
      } else {
        const nextNode = Editor.next(editor, {
          at: maybeBracketLocation,
          match: (n) =>
            Element.isElement(n) && n.type === PARAGRAPH_PLUGIN_ID && n.shouldForceInline === true,
        });
        const startOfNextNode = nextNode != null ? Editor.start(editor, nextNode[1]) : null;
        return [
          fragment,
          startOfNextNode ?? [maybeBracketLocation[0], maybeBracketLocation[1] + 1],
        ];
      }
    } else if (!isInsideBrackets) {
      return [fragment, insertionPoint];
    }

    if (Element.isElement(maybeBracketNode) && maybeBracketNode.type === PLACEHOLDER_PLUGIN_ID) {
      const locationAfter = Editor.after(editor, maybeBracketLocation);
      return [fragment, locationAfter ?? [maybeBracketLocation[0], maybeBracketLocation[1] + 1]];
    }
  }

  return [fragment, insertionPoint];
};

export const getFirstInlineBookmarkInPath = (
  editor: Editor,
  start: Path = [],
  end: Path = []
): NodeEntry<Node> | null => {
  const maybeFirstInlineParagraph = Editor.nodes(editor, {
    at: [start, end],
    match: (node: Node) => Element.isElement(node) && node.type === INLINE_BOOKMARK_PLUGIN_ID,
  }).next();
  if (!maybeFirstInlineParagraph.done) {
    return maybeFirstInlineParagraph.value;
  }
  return null;
};

export const getInlineBookmarkInSameParagraph = (
  editor: Editor,
  path: Path
): NodeEntry<Node> | null => {
  const parentParagraph = Editor.above(editor, {
    at: path,
    match: (node: Node) => Element.isElement(node) && node.type === PARAGRAPH_PLUGIN_ID,
  });

  if (parentParagraph == null) {
    return null;
  }

  return getFirstInlineBookmarkInPath(editor, parentParagraph[1], parentParagraph[1]);
};

export const getLastInlineBookmarkInRange = (
  editor: Editor,
  range: Selection
): Nullable<NodeEntry> => {
  if (range == null) return null;

  const maybeInlineBookmark = Editor.nodes(editor, {
    at: range,
    reverse: true,
    match: (n) => {
      return Element.isElement(n) && n.type === INLINE_BOOKMARK_PLUGIN_ID;
    },
  }).next();

  if (!maybeInlineBookmark.done && maybeInlineBookmark.value != null) {
    return maybeInlineBookmark.value as NodeEntry;
  }

  return null;
};

export const getHasMultipleInlineBookmarksInRange = (editor: Editor, range: Selection): boolean => {
  if (range == null) return false;

  let count = 0;

  for (const node of Array.from(
    Editor.nodes(editor, {
      at: range,
      match: (n) => Element.isElement(n) && n.type === INLINE_BOOKMARK_PLUGIN_ID,
    })
  )) {
    count++;
    if (count > 1) {
      return true;
    }
  }

  return false;
};

export const getLastInlineBookmarkInRangeIfEmpty = (
  editor: Editor,
  range: Selection
): Nullable<NodeEntry> => {
  if (range == null) return null;

  const maybeInlineBookmark = Editor.nodes(editor, {
    at: range,
    reverse: true,
    match: (n) => {
      return Element.isElement(n) && n.type === INLINE_BOOKMARK_PLUGIN_ID;
    },
  }).next();

  if (
    !maybeInlineBookmark.done &&
    maybeInlineBookmark.value != null &&
    Node.string(maybeInlineBookmark.value[0]).trim() === ''
  ) {
    return maybeInlineBookmark.value;
  }

  return null;
};

export type TemplateBookmarkTextMap = Map<string, string>;

// Only ever call this with *template* content (never live editor content) — bookmark children
// can hold non-text nodes (e.g. deep-link elements, see deepLink/utils.ts) and Node.string would
// silently fold that text in.
export const buildBookmarkTextMap = (content: Nullable<SlateContent>): TemplateBookmarkTextMap => {
  const map: TemplateBookmarkTextMap = new Map();
  if (content == null) return map;
  walkSlateContent((node) => {
    if (!Element.isElement(node) || node.type !== INLINE_BOOKMARK_PLUGIN_ID) return;
    const { name } = node as InlineBookmarkPluginElement;
    if (name == null) return;
    map.set(name, Node.string(node));
  })(content);
  return map;
};

const normalizeForComparison = (text: string): string => text.trim().toLowerCase();

// Exact-match comparison against the template's own text for this bookmark name — distinct in
// purpose from isReplaceableDefaultComparisonText's fuzzy whitelist match (which also strips
// trailing periods); this only needs to know if the text is unchanged from what was inserted.
export const isBookmarkTextUnchangedFromTemplate = (
  name: string | undefined,
  currentText: string,
  templateMap: TemplateBookmarkTextMap
): boolean => {
  if (name == null) return false;
  const templateText = templateMap.get(name);
  if (templateText == null) return false;
  return normalizeForComparison(currentText) === normalizeForComparison(templateText);
};
